import {
  AdminDepartmentApi,
  AdminHospitalApi,
  AdminWardApi,
  type GetHospitalAdminsResponse,
  type GetHospitalsResponse,
  type GetLinkedHospitalsResponse,
  type HospitalCreateDTO,
  type HospitalDetailsDTO,
  type HospitalUpdateDTO,
  type UpsertLinkedHospitalsDTO,
  type UpdateDepartmentsWithWardsRequest,
  WardTypeDTO
} from '@/generated'
import { globalApiConfig } from '@/services/telehealthApi/APIConfig'

export class AdminHospitalService {
  static readonly adminHospitalApi = new AdminHospitalApi(globalApiConfig)
  static readonly adminDepartmentApi = new AdminDepartmentApi(globalApiConfig)
  static readonly adminWardApi = new AdminWardApi(globalApiConfig)

  static async getHospitalById(hospitalId: string): Promise<HospitalDetailsDTO> {
    return (await AdminHospitalService.adminHospitalApi.getHospital(hospitalId)).data
  }

  static async getHospitals(offset: number, limit: number): Promise<GetHospitalsResponse> {
    return (await AdminHospitalService.adminHospitalApi.getHospitals(offset, limit)).data
  }

  static async getHospitalAdminCandidates(hospitalId: string): Promise<GetHospitalAdminsResponse> {
    return (await AdminHospitalService.adminHospitalApi.getHospitalAdminCandidates(hospitalId)).data
  }

  static async createHospital(hospital: HospitalCreateDTO): Promise<void> {
    await AdminHospitalService.adminHospitalApi.createHospital(hospital)
  }

  static async updateHospital(hospitalId: string, hospital: HospitalUpdateDTO): Promise<void> {
    await AdminHospitalService.adminHospitalApi.updateHospital(hospitalId, hospital)
  }

  static async updateHospitalAdmins(hospitalId: string, hospitalAdmins: string[]): Promise<void> {
    await AdminHospitalService.adminHospitalApi.updateHospitalAdmins(hospitalId, {
      userNames: hospitalAdmins
    })
  }

  static async addDepartmentsToHospital(
    hospitalId: string,
    departmentNames: string[]
  ): Promise<void> {
    await AdminHospitalService.adminDepartmentApi.createDepartments({
      departments: departmentNames.map((departmentName) => ({
        hospitalId,
        name: departmentName
      }))
    })
  }

  static async addWardsToDepartment(
    hospitalId: string,
    departmentId: string,
    wardNames: string[]
  ): Promise<void> {
    await AdminHospitalService.adminWardApi.createWards({
      wards: wardNames.map((wardName) => ({
        hospitalId,
        departmentId,
        name: wardName,
        wardType: WardTypeDTO.ICU,
        tccBooked: false
      }))
    })
  }

  static async updateHospitalDepartmentsAndWards(
    updateDepartmentsWithWardsRequest: UpdateDepartmentsWithWardsRequest
  ): Promise<void> {
    await AdminHospitalService.adminDepartmentApi.updateDepartmentsWithWards(
      updateDepartmentsWithWardsRequest
    )
  }

  static async getLinkedHospitals(hospitalId: string): Promise<GetLinkedHospitalsResponse> {
    return (await AdminHospitalService.adminHospitalApi.getLinkedHospitals(hospitalId)).data
  }

  static async updateLinkedHospitals(
    hospitalId: string,
    hospitalsDTO: UpsertLinkedHospitalsDTO
  ): Promise<void> {
    await AdminHospitalService.adminHospitalApi.updateLinkedHospitals(hospitalId, hospitalsDTO)
  }
}
